import { ReactNode } from "react"
import { Navigate, useLocation } from "react-router-dom"

import { USER_ACCOUNT_LOADING_STATUS } from "../../modules/ClientAuthorization/Default/common/constant"

import { Spinner } from "components/Spinner"
import { RedirectToAuth } from "modules/ClientAccount/components/RedirectToAuth"
import { ClientProvider, useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { AuthContextProvider, useAuthContext } from "modules/PsychologistAccount/AuthContext"
import { ROLES, RoleType } from "utils/constants"

interface ProtectedRouteProps {
    children: ReactNode
    isAuthorized?: boolean
    redirectComponent?: JSX.Element
}

function ProtectedRoute({
    isAuthorized,
    children,
    redirectComponent = <RedirectToAuth />,
}: ProtectedRouteProps) {
    return isAuthorized ? <>{children}</> : redirectComponent
}

function ClientPrivateRoute({ children }: { children: ReactNode }) {
    const [{ isVerified, loading }] = useClientContext()

    if (loading !== USER_ACCOUNT_LOADING_STATUS.READY) {
        return <Spinner />
    }

    return <ProtectedRoute isAuthorized={isVerified}>{children}</ProtectedRoute>
}

function PsyPrivateRoute({
    children,
    redirectComponent,
}: {
    children: ReactNode
    redirectComponent?: JSX.Element
}) {
    const [, { isSignedIn }] = useAuthContext()

    return (
        <ProtectedRoute isAuthorized={isSignedIn} redirectComponent={redirectComponent}>
            {children}
        </ProtectedRoute>
    )
}

interface PrivateRouteProps {
    roles: RoleType[]
    children: ReactNode
}

export function PrivateRoute({ roles, children }: PrivateRouteProps) {
    const location = useLocation()

    let content: ReactNode = <Navigate to="/" state={{ from: location }} replace />

    if (roles.includes(ROLES.CLIENT) && roles.includes(ROLES.PSY)) {
        content = (
            <AuthContextProvider>
                <ClientProvider>
                    <PsyPrivateRoute
                        redirectComponent={
                            <AuthContextProvider>
                                <ClientPrivateRoute>{children}</ClientPrivateRoute>
                            </AuthContextProvider>
                        }
                    >
                        {children}
                    </PsyPrivateRoute>
                </ClientProvider>
            </AuthContextProvider>
        )
    } else if (roles.includes(ROLES.CLIENT)) {
        content = <ClientPrivateRoute>{children}</ClientPrivateRoute>
    } else if (roles.includes(ROLES.PSY)) {
        content = (
            <AuthContextProvider>
                <PsyPrivateRoute>{children}</PsyPrivateRoute>
            </AuthContextProvider>
        )
    }

    return content
}
